import { useEffect } from 'react';
import { useFieldJob } from '../lib/field-job.js';
import { useActiveJob } from '../lib/field-time.js';
import type { FieldPhase } from '../lib/api.js';
import { DayScreen } from '../components/field/day-screen.js';
import { OnwayScreen } from '../components/field/onway-screen.js';
import { OnsiteScreen } from '../components/field/onsite-screen.js';
import { SignScreen } from '../components/field/sign-screen.js';
import { CompleteScreen } from '../components/field/complete-screen.js';

/**
 * /jobs — the field-tech my-day flow.
 *
 * Routes by `useFieldJob().screen`, but reconciles to the server-side
 * active-job state on mount so a tech who locked their phone (or just
 * re-opened the tab) lands directly on the right screen for whatever
 * phase the server says they're in. Without this, the local sessionStorage
 * state can show "day" while the server holds an open TimeEntry — and
 * tapping the card on the day list would restart the entry from
 * `traveling`, wiping the existing phase + time history.
 */
export function JobsScreen() {
  useFieldFlowReconciliation();
  const screen = useFieldJob((s) => s.screen);

  switch (screen) {
    case 'day':
      return <DayScreen />;
    case 'onway':
      return <OnwayScreen />;
    case 'onsite':
      return <OnsiteScreen />;
    case 'sign':
      return <SignScreen />;
    case 'complete':
      return <CompleteScreen />;
  }
}

/** Map a server-side phase to the local screen-flow state that owns it. */
function screenForPhase(phase: FieldPhase): 'onway' | 'onsite' | 'day' {
  switch (phase) {
    case 'traveling':
      return 'onway';
    case 'on_site':
    case 'work_in_progress':
    case 'paused':
    case 'work_completed':
      return 'onsite';
    case 'scheduled':
    case 'signed_off':
      return 'day';
  }
}

/**
 * Sync local zustand to server-side active-job state.
 *
 *   1. Server says "active": adopt its order id / orderNo and navigate to
 *      the local screen that owns the current phase.
 *   2. Server says null but local has currentOrderId: local state is
 *      stale (server stopped the entry from another device, or a stop
 *      mutation landed after a tab refresh) — reset to the day list.
 *
 * Runs once per activeQuery resolution. We intentionally don't loop on
 * every local-state change because the user actions inside Onway /
 * Onsite drive their own transitions; this effect only catches the
 * "tab just mounted" or "server diverged" cases.
 */
function useFieldFlowReconciliation(): void {
  const activeQuery = useActiveJob();
  const screen = useFieldJob((s) => s.screen);
  const currentOrderId = useFieldJob((s) => s.currentOrderId);
  const startJob = useFieldJob((s) => s.startJob);
  const goOnsite = useFieldJob((s) => s.goOnsite);
  const reset = useFieldJob((s) => s.reset);
  const hasCaptures = useFieldJob(
    (s) => s.parts.length > 0 || s.photos.length > 0 || s.signed,
  );

  // Stable ref to the active job's identity so we only trigger a sync
  // when the server-side identity actually changes — not on every
  // 30s refetch that returns the same data.
  const active = activeQuery.data ?? null;
  const activeKey = active
    ? `${active.activeJobSpireId}:${active.currentPhase}`
    : null;

  useEffect(() => {
    if (activeQuery.isPending) return;

    if (!active) {
      // Server has no active job. Local stale-state cleanup: if the
      // tech is sitting on a sub-screen with a leftover currentOrderId
      // (closed from another device, or after a successful stop on a
      // previous tab), put them back on the day list. Leave them alone
      // on the 'complete' screen — that's a deliberate post-clockout
      // resting state until they hit "Back to my day".
      if (currentOrderId !== null && screen !== 'complete' && screen !== 'day') {
        // NEVER wipe locally-captured parts/photos/signature just because
        // the server entry closed (audit P0-7): the most common way to get
        // here is a clock-out where `stop` succeeded and `markComplete`
        // failed — the tech is mid-retry with a visible error banner, and
        // a silent reset() would destroy the visit's evidence AND the
        // retry path. With captures present we leave them where they are;
        // the empty case (nothing to lose) still resets to Day.
        if (!hasCaptures) {
          reset();
        }
      }
      return;
    }

    // Server has an open entry. Only auto-transition the screen when the
    // server identity diverges from local. If they ALREADY match (e.g. the
    // tech intentionally tapped Back to peek their day list), leave them
    // wherever they parked themselves — re-tapping the live card on Day
    // is what triggers the explicit screen flip.
    if (currentOrderId !== active.activeJobSpireId) {
      startJob(active.activeJobSpireId, active.activeJobOrderNo);
      const desiredScreen = screenForPhase(active.currentPhase);
      if (desiredScreen === 'onsite' && screen !== 'sign') {
        goOnsite();
      }
      // 'onway' is already what startJob() set; 'sign' is sub-state of
      // onsite that the tech opted into and we never overwrite.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, activeQuery.isPending]);
}
